/**
 * Frontend-owned subject grouping for the permission matrix: the backend's
 * `GET /subjects` returns a flat list with no presentation data. Unmapped
 * subjects fall under "Other" so a new backend subject is never silently hidden.
 *
 * Per-tenant amendments (hidden rows + label renames) are layered on here from
 * `clientConfig.accessControl` — the shared contract enum stays the superset;
 * this module is the single presentation seam that trims/relabels it per brand.
 */
import { clientConfig } from '@/config/clientConfig';

interface SubjectGroupDefinition {
  label: string;
  subjects: string[];
}

/** Ordered group definitions. Order drives the matrix section order. */
const GROUP_DEFINITIONS: SubjectGroupDefinition[] = [
  { label: 'Jobs & Work', subjects: ['Job', 'WorkOrder', 'Planner'] },
  { label: 'People', subjects: ['Team', 'User', 'Shift'] },
  {
    // WRK-5669: the per-entity configuration subjects split out of the coarse `Settings` umbrella.
    label: 'Configuration',
    subjects: [
      'JobCategory',
      'TaskType',
      'Form',
      'FormTransfer',
      'Area',
      'JobTitle',
      'Discipline',
      'Skill',
      'LeaveType',
    ],
  },
  {
    label: 'Administration',
    subjects: ['Settings', 'AuditTrail', 'AccessControl', 'Report'],
  },
];

const OTHER_GROUP_LABEL = 'Other';

/**
 * Human-friendly display names for subjects whose raw value reads poorly in the UI. The
 * subject *value* (persisted in the DB and carried over the API) stays unchanged — this
 * only affects how the matrix labels the row. Subjects not listed here fall back to their
 * raw value (already readable, e.g. `Job`, `Team`, `Settings`).
 */
const SUBJECT_LABELS: Record<string, string> = {
  Report: 'Analytics',
  AuditTrail: 'Audit Trail',
  WorkOrder: 'Work Order',
  AccessControl: 'Access Control',
  // The `Planner` subject drives the Schedule surface; label it "Scheduler" for admins.
  Planner: 'Scheduler',
  // WRK-5669 per-entity config subjects whose raw value reads poorly.
  JobCategory: 'Job Category',
  TaskType: 'Task Type',
  LeaveType: 'Leave Type',
  JobTitle: 'Job Title',
  FormTransfer: 'Form Import / Export',
};

/**
 * The display label for a subject. A per-tenant override (clientConfig) wins, then
 * the global friendly label, then the raw value. The subject *value* is never
 * changed — this only affects the matrix row text.
 */
export function subjectLabel(subject: string): string {
  return (
    clientConfig.accessControl.subjectLabelOverrides[subject] ?? SUBJECT_LABELS[subject] ?? subject
  );
}

export interface SubjectGroup {
  label: string;
  subjects: string[];
}

/**
 * Groups a flat subject list into ordered, labelled sections (empty groups dropped).
 * Subjects listed in `clientConfig.accessControl.hiddenSubjects` are removed first, so
 * a brand can present a trimmed matrix. Hiding is cosmetic: it never revokes a grant or
 * a backend guard — see {@link ClientAccessControlConfig}.
 */
export function groupSubjects(subjects: string[]): SubjectGroup[] {
  const hidden = new Set(clientConfig.accessControl.hiddenSubjects);
  const visible = subjects.filter((subject) => !hidden.has(subject));
  const remaining = new Set(visible);
  const groups: SubjectGroup[] = [];

  for (const def of GROUP_DEFINITIONS) {
    const present = def.subjects.filter((subject) => remaining.has(subject));
    for (const subject of present) remaining.delete(subject);
    if (present.length > 0) {
      groups.push({ label: def.label, subjects: present });
    }
  }

  if (remaining.size > 0) {
    // Preserve the backend's order for any unmapped subjects.
    const leftovers = visible.filter((subject) => remaining.has(subject));
    groups.push({ label: OTHER_GROUP_LABEL, subjects: leftovers });
  }

  return groups;
}
